import React, { useEffect, useRef, useState } from "react";
import styles from "../styles/Partner2Page.module.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import brand from "../assets/brand.svg";
import { useLocation } from "react-router-dom";
import {
  FiUser,
  FiBriefcase,
  FiPieChart,
  FiLayers,
  FiCheck,
  FiArrowRight,
  FiArrowLeft,
  FiMail,
} from "react-icons/fi";

const PARTNER_EMAIL = "info@arifpay.net";

const STEPS = [
  { id: 1, label: "You", icon: FiUser },
  { id: 2, label: "Company", icon: FiBriefcase },
  { id: 3, label: "Market", icon: FiPieChart },
  { id: 4, label: "Interest", icon: FiLayers },
];

const MARKETS = [
  "Healthcare",
  "Hospitality and Entertainment",
  "Financial Services",
  "Logistics and Distribution",
  "Travel and Transportation",
  "Government Services",
  "Development Partners",
  "FMCG(Consumer Goods)",
  "Other",
];

const PARTNERSHIPS = [
  { value: "resell", label: "Resell Arifpay products." },
  { value: "add_value", label: "Add value to Arifpay products and resell." },
  { value: "integrate", label: "Integrate Arifpay solutions to other software solutions." },
  { value: "repair", label: "Repair, maintain or support Arifpay end users systems." },
  { value: "provide_services", label: "Provide services/solutions on behalf of Arifpay." },
  { value: "embed", label: "Embed Arifpay into my solutions & sell them to end users." },
  { value: "campaign", label: "Be Arifpay's campaign and sales force." },
  { value: "introduce", label: "Introduce Arifpay to potential partners." },
  { value: "other", label: "Other (Please specify)" },
];

function partnershipLabel(value, other) {
  if (value === "other") return other.trim() || "Other";
  return PARTNERSHIPS.find((item) => item.value === value)?.label || "Not specified";
}

function buildPartnerMailto({
  name,
  email,
  phone,
  companyName,
  role,
  website,
  hasLicense,
  marketSegment,
  partnershipType,
  otherText,
}) {
  const subject = `Partnership application from ${companyName.trim() || name.trim()}`;
  const body = [
    "Partnership application",
    "",
    "Applicant",
    `Name: ${name.trim()}`,
    `Email: ${email.trim()}`,
    `Phone: ${phone.trim()}`,
    "",
    "Company",
    `Company name: ${companyName.trim()}`,
    `Role: ${role.trim()}`,
    `Website: ${website.trim()}`,
    `Valid business license: ${hasLicense ? "Yes" : "No"}`,
    "",
    "Market",
    `Market segment: ${marketSegment || "Not specified"}`,
    "",
    "Interest",
    `Partnership type: ${partnershipLabel(partnershipType, otherText)}`,
  ].join("\n");

  return `mailto:${PARTNER_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function openMailto(href) {
  const link = document.createElement("a");
  link.href = href;
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function toLocalPhone(value) {
  const compact = value.replace(/[\s()-]/g, "");
  const withoutPlus = compact.startsWith("+") ? compact.slice(1) : compact;
  if (withoutPlus.startsWith("251")) {
    return `0${withoutPlus.slice(3)}`;
  }
  return value;
}

function Choice({ name, value, checked, onChange, children }) {
  return (
    <label className={`${styles.choice} ${checked ? styles.choiceOn : ""}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
      />
      <span className={styles.choiceMark} aria-hidden="true" />
      <span className={styles.choiceText}>{children}</span>
    </label>
  );
}

const BecomeAPartner = () => {
  const location = useLocation();
  const formRef = useRef(null);
  const previousStep = useRef(1);

  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState({});

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [role, setRole] = useState("");
  const [website, setWebsite] = useState("");
  const [hasLicense, setHasLicense] = useState(false);

  const [marketSegment, setMarketSegment] = useState(null);
  const [partnershipType, setPartnershipType] = useState(null);
  const [otherText, setOtherText] = useState("");

  useEffect(() => {
    const previousRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    return () => {
      history.scrollRestoration = previousRestoration;
    };
  }, [location]);

  useEffect(() => {
    if (previousStep.current === currentStep) return;
    previousStep.current = currentStep;
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentStep]);

  const validateStep1 = () => {
    const newErrors = {};
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = "Name is required.";
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = "Email Address is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email Address is invalid.";
      isValid = false;
    }

    if (!phone.trim()) {
      newErrors.phone = "Phone Number is required.";
      isValid = false;
    } else if (!/^\d+$/.test(phone)) {
      newErrors.phone = "Phone Number must contain only digits.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateStep2 = () => {
    const newErrors = {};
    let isValid = true;

    if (!companyName.trim()) {
      newErrors.companyName = "Company Name is required.";
      isValid = false;
    }

    if (!role.trim()) {
      newErrors.role = "Your Role at the company is required.";
      isValid = false;
    }

    if (!website.trim()) {
      newErrors.website = "Website is required.";
      isValid = false;
    } else if (!/^https?:\/\/\S+\.\S+/.test(website)) {
      newErrors.website =
        "Website must start with http:// or https:// and be a valid URL.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validateStep3 = () => {
    if (marketSegment === null) {
      setErrors({
        selectedInterest: "Please select at least one market segment.",
      });
      return false;
    }
    setErrors({});
    return true;
  };

  const validateStep4 = () => {
    if (!partnershipType) {
      setErrors({
        partnershipType: "Please select a partnership type.",
      });
      return false;
    }
    if (partnershipType === "other" && !otherText.trim()) {
      setErrors({ otherText: "Please specify your interest." });
      return false;
    }
    setErrors({});
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;
    if (currentStep === 4) {
      if (!validateStep4()) return;
      openMailto(
        buildPartnerMailto({
          name,
          email,
          phone,
          companyName,
          role,
          website,
          hasLicense,
          marketSegment,
          partnershipType,
          otherText,
        })
      );
      setShowConfirmation(true);
      return;
    }
    setErrors({});
    setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep === 1) return;
    setErrors({});
    setCurrentStep(currentStep - 1);
  };

  const renderStep = () => {
    if (currentStep === 1) {
      return (
        <>
          <StepHeading
            icon={<FiUser />}
            kicker="Step 1 of 4"
            title="Tell Us About Yourself"
            text="We will use these details to follow up on your application."
          />
          <div className={styles.stack}>
            <Field
              id="name"
              label="Name:"
              error={errors.name}
              value={name}
              onChange={setName}
              placeholder="John doe"
              autoComplete="name"
            />
            <Field
              id="email"
              label="Email Address:"
              error={errors.email}
              value={email}
              onChange={setEmail}
              placeholder="example@email.com"
              type="email"
              autoComplete="email"
            />
            <Field
              id="phone"
              label="Phone Number:"
              error={errors.phone}
              value={phone}
              onChange={(value) => setPhone(toLocalPhone(value))}
              onBlur={(value) => setPhone(toLocalPhone(value))}
              placeholder="0912345445"
              type="tel"
              autoComplete="tel"
              inputMode="numeric"
            />
          </div>
        </>
      );
    }

    if (currentStep === 2) {
      return (
        <>
          <StepHeading
            icon={<FiBriefcase />}
            kicker="Step 2 of 4"
            title="Tell Us About Your company"
            text="Tell us where you work and how to find the business online."
          />
          <div className={styles.stack}>
            <div className={styles.group}>
              <p className={styles.groupLabel}>
                Please Tick If You Have a Valid Business License.
              </p>
              <label className={styles.checkRow} htmlFor="hasLicense">
                <input
                  type="checkbox"
                  id="hasLicense"
                  checked={hasLicense}
                  onChange={(e) => setHasLicense(e.target.checked)}
                />
                <span>I have a valid business license:</span>
              </label>
            </div>
            <Field
              id="companyName"
              label="Company Name:"
              error={errors.companyName}
              value={companyName}
              onChange={setCompanyName}
              placeholder="Your Company Name"
              autoComplete="organization"
            />
            <Field
              id="role"
              label="Your Role at the company:"
              error={errors.role}
              value={role}
              onChange={setRole}
              placeholder="Manager"
            />
            <Field
              id="website"
              label="Website:"
              error={errors.website}
              value={website}
              onChange={setWebsite}
              placeholder="https://www.yourwebsite.com"
              type="url"
              hint='Please fill the website with this format "https://..." or "http://...".'
            />
          </div>
        </>
      );
    }

    if (currentStep === 3) {
      return (
        <>
          <StepHeading
            icon={<FiPieChart />}
            kicker="Step 3 of 4"
            title="Tell us about your market segment"
            text="Choose the market that best describes your business."
          />
          <div className={styles.choiceGrid}>
            {MARKETS.map((interestValue) => (
              <Choice
                key={interestValue}
                name="market"
                value={interestValue}
                checked={marketSegment === interestValue}
                onChange={() => setMarketSegment(interestValue)}
              >
                {interestValue}
              </Choice>
            ))}
          </div>
          {errors.selectedInterest && (
            <p className={styles.errorMessage}>{errors.selectedInterest}</p>
          )}
        </>
      );
    }

    return (
      <>
        <StepHeading
          icon={<FiLayers />}
          kicker="Step 4 of 4"
          title="What are you primarily interested in?"
          text={`Sending opens your email app with this application addressed to ${PARTNER_EMAIL}.`}
        />
        <div className={styles.choiceGrid}>
          {PARTNERSHIPS.map((item) => (
            <Choice
              key={item.value}
              name="partnership"
              value={item.value}
              checked={partnershipType === item.value}
              onChange={() => setPartnershipType(item.value)}
            >
              {item.label}
            </Choice>
          ))}
        </div>
        {errors.partnershipType && (
          <p className={styles.errorMessage}>{errors.partnershipType}</p>
        )}
        {partnershipType === "other" && (
          <div className={styles.group}>
            <Field
              id="otherInterest"
              label="Please specify"
              error={errors.otherText}
              value={otherText}
              onChange={setOtherText}
              placeholder="Please specify your interest"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.page}>
      <Header />
      <div className={styles.shell}>
        <div className={styles.layout}>
          <aside className={styles.aside}>
            <div className={styles.brandRow}>
              <img src={brand} alt="" className={styles.logo} />
              <p className={styles.badge}>Partnership</p>
            </div>
            <h1 className={styles.title}>Become a Partner</h1>
            <p className={styles.lead}>
              Complete these four steps on this page. On the last step, your
              application opens in your email app, addressed to {PARTNER_EMAIL}.
            </p>
            <ul className={styles.checklist}>
              {STEPS.map((step) => (
                <li
                  key={step.id}
                  className={
                    currentStep > step.id
                      ? styles.done
                      : currentStep === step.id
                      ? styles.current
                      : ""
                  }
                >
                  <FiCheck />
                  {step.label}
                  {currentStep === step.id ? " · in progress" : ""}
                </li>
              ))}
            </ul>
          </aside>

          <main className={styles.card} ref={formRef}>
            <ol className={styles.stepper}>
              {STEPS.map((step) => (
                <li
                  key={step.id}
                  className={`${styles.step} ${
                    currentStep === step.id
                      ? styles.active
                      : currentStep > step.id
                      ? styles.completed
                      : ""
                  }`}
                  aria-current={currentStep === step.id ? "step" : undefined}
                >
                  <span className={styles.stepNumber}>
                    {currentStep > step.id ? <FiCheck /> : step.id}
                  </span>
                  <span className={styles.stepLabel}>{step.label}</span>
                </li>
              ))}
            </ol>
            <div className={styles.track} aria-hidden="true">
              <div
                className={styles.trackFill}
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>

            {renderStep()}

            <div className={styles.actions}>
              {currentStep > 1 ? (
                <button
                  type="button"
                  className={styles.previousButton}
                  onClick={handlePrevious}
                >
                  <FiArrowLeft /> Previous
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                className={styles.nextButton}
                onClick={handleNext}
              >
                {currentStep === STEPS.length ? (
                  <>
                    Send application <FiMail />
                  </>
                ) : (
                  <>
                    Next <FiArrowRight />
                  </>
                )}
              </button>
            </div>
          </main>
        </div>
      </div>

      {showConfirmation && (
        <div className={styles.overlay} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <div className={styles.modalIcon}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                />
              </svg>
            </div>
            <p className={styles.modalKicker}>EMAIL READY</p>
            <h2>Send your application</h2>
            <p>
              Your email app should now be open with this application addressed
              to {PARTNER_EMAIL}. Send that message to finish. We will review
              it and get back to you.
            </p>
            <button type="button" onClick={() => setShowConfirmation(false)}>
              Done
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

function StepHeading({ icon, kicker, title, text }) {
  return (
    <div className={styles.stepHead}>
      <span className={styles.stepIcon}>{icon}</span>
      <div>
        <p className={styles.kicker}>{kicker}</p>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <p className={styles.sectionText}>{text}</p>
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  error,
  hint,
  value,
  onChange,
  placeholder,
  type = "text",
  autoComplete,
  inputMode,
  onBlur,
}) {
  return (
    <div className={styles.field}>
      <label htmlFor={id}>{label}</label>
      {hint ? <p className={styles.hint}>{hint}</p> : null}
      <input
        type={type}
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
        className={error ? styles.inputError : ""}
        autoComplete={autoComplete}
        inputMode={inputMode}
      />
      {error ? <p className={styles.errorMessage}>{error}</p> : null}
    </div>
  );
}

export default BecomeAPartner;
